import { css } from 'lit';

/*
  Data table visual theme.

  Pulls from the app's global token palette so it matches the rest of the
  Pag-asenso UI without any per-page overrides:

    --app-bg / --app-surface / --app-text / --app-text-muted / --app-border
    --app-shadow-sm / --app-shadow-md
    --brand-rose-{50..950} (mapped to --sl-color-primary-*)
    --sl-border-radius-{small,medium,large}
    --font-family

  Each visual is exposed as a --data-table-* token first, defaulted to one
  of the app tokens. That gives consumers a clean override surface (e.g.
  a denser embedded table) without forking the stylesheet.
*/

export const dataTableStyles = css`
  :host {
    --data-table-surface: var(--app-surface, #ffffff);
    --data-table-surface-muted: var(--sl-color-neutral-50, #f8fafc);
    --data-table-border: var(--app-border, rgba(15, 23, 42, 0.08));
    --data-table-divider: var(--app-border, rgba(15, 23, 42, 0.06));
    --data-table-text: var(--app-text, var(--sl-color-neutral-900));
    --data-table-text-muted: var(--app-text-muted, var(--sl-color-neutral-600));

    --data-table-header-bg: var(--sl-color-neutral-100);
    --data-table-header-color: var(
      --app-text-muted,
      var(--sl-color-neutral-600)
    );
    --data-table-header-border: var(--app-border, rgba(15, 23, 42, 0.08));

    --data-table-row-hover: var(--brand-rose-50, #fff1f6);
    --data-table-row-selected: var(--brand-rose-100, #ffe1ec);
    --data-table-row-selected-accent: var(--brand-rose-500, #e1477e);

    --data-table-row-extension-bg: var(
      --sl-color-neutral-50,
      var(--app-bg, #fbf7f4)
    );

    --data-table-accent: var(--brand-rose-500, #e1477e);
    --data-table-accent-strong: var(--brand-rose-600, #c52e66);
    --data-table-accent-soft: var(--brand-rose-100, #ffe1ec);

    --data-table-radius: var(--sl-border-radius-large, 14px);
    --data-table-shadow: var(--app-shadow-sm, 0 1px 2px rgba(15, 23, 42, 0.04));

    --data-table-font: var(
      --font-family,
      'Inter',
      'Inter Variable',
      -apple-system,
      BlinkMacSystemFont,
      'Segoe UI',
      Roboto,
      sans-serif
    );

    --data-table-row-shadow-fixed: 6px 0 10px -6px rgba(15, 23, 42, 0.08);
    --data-table-row-shadow-fixed-right: -6px 0 10px -6px rgba(15, 23, 42, 0.08);

    /*
      Lift Shoelace dropdown / popup z-index above the sticky table
      header (z-index: 10 below). Without this, hoisted listboxes
      from <sl-select hoist> render behind the header when they cross
      it on screen.
    */
    --sl-z-index-dropdown: 1100;

    display: block;
    width: 100%;
    color: var(--data-table-text);
  }

  /* When placed in a flex container, fill available height */
  :host([fill-height]) {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 0;
    overflow: hidden;
  }

  :host([fill-height]) .data-table.data-table {
    flex: 1 1 auto;
    min-height: 0;
  }

  :host([fill-height]) .data-table-scroll {
    max-height: none !important;
    flex: 1 1 auto;
    min-height: 0;
    overflow: auto;
  }

  .data-table.data-table {
    display: flex;
    flex-direction: column;
    background: var(--data-table-surface);
    border: 1px solid var(--data-table-border);
    border-radius: var(--data-table-radius);
    box-shadow: var(--data-table-shadow);
    overflow: hidden;
    overflow-anchor: none;
    font-family: var(--data-table-font);
  }

  /* ---------- Header ---------- */

  .data-table-header {
    background: var(--data-table-header-bg);
    color: var(--data-table-header-color);
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
    width: 100%;
    min-width: max-content;
    border-bottom: 1px solid var(--data-table-header-border);
  }

  .data-table-header--sticky {
    box-shadow: 0 1px 0 var(--data-table-header-border);
  }

  .data-table-header-row {
    display: flex;
    align-items: stretch;
    min-height: 2.25rem;
    width: 100%;
    min-width: max-content;
    font-size: 0.72rem;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    font-weight: 600;
    will-change: transform;
  }

  .data-table-header-segment {
    display: flex;
    position: relative;
    z-index: 1;
    background: var(--data-table-header-bg);
  }

  .data-table-header-segment-left {
    flex: 0 0 auto;
    position: sticky;
    left: 0;
    z-index: 3;
    box-shadow: var(--data-table-row-shadow-fixed);
  }

  .data-table-header-segment-right {
    flex: 0 0 auto;
    width: max-content;
    position: sticky;
    right: 0;
    z-index: 3;
    box-shadow: var(--data-table-row-shadow-fixed-right);
  }

  .data-table-header-segment-center {
    flex: 1 1 auto;
    position: relative;
    /*
      No explicit z-index: would create a stacking context that traps
      hoisted Shoelace popups (sl-select, sl-tooltip, etc.) below sticky
      siblings. Sticky left/right segments still layer correctly via
      their own z-index.
    */
  }

  .data-table-header-center-track {
    display: flex;
  }

  /* ---------- Rows ---------- */

  .data-table-row {
    display: flex;
    align-items: stretch;
    min-height: 2.25rem;
    width: 100%;
    min-width: max-content;
    position: relative;
    /*
      Reserve 3px on the left for the selected-row accent stripe. Keeping
      it transparent on unselected rows means selected and unselected
      rows have identical layout — switching selection never shifts
      column positions. The previous inset box-shadow drew over the
      leftmost cell content, which read as a layout shift.
    */
    border-left: 3px solid transparent;
    border-bottom: 1px solid var(--data-table-divider);
    background: var(--data-table-surface);
    transition:
      background 140ms ease,
      border-color 140ms ease;
  }

  .data-table-row:last-of-type {
    border-bottom: none;
  }

  .data-table-row-segment {
    display: flex;
    position: relative;
  }

  .data-table-row-segment-left {
    flex: 0 0 auto;
    z-index: 2;
    background: var(--data-table-surface);
    position: sticky;
    left: 0;
    box-shadow: var(--data-table-row-shadow-fixed);
  }

  .data-table-row-segment-right {
    flex: 0 0 auto;
    z-index: 2;
    background: var(--data-table-surface);
    position: sticky;
    right: 0;
    box-shadow: var(--data-table-row-shadow-fixed-right);
    margin-left: auto;
  }

  .data-table-row:hover .data-table-row-segment-left,
  .data-table-row:hover .data-table-row-segment-right {
    background: var(--data-table-row-hover);
  }

  .data-table-row.selected .data-table-row-segment-left,
  .data-table-row.selected .data-table-row-segment-right {
    background: var(--data-table-row-selected);
  }

  .data-table-row-segment-center {
    flex: 1 1 auto;
    position: relative;
    /* No explicit z-index — see header-segment-center note above. */
  }

  .data-table-row-center-track {
    display: flex;
  }

  .data-table-row:hover {
    background: var(--data-table-row-hover);
  }

  .data-table-row.selected {
    background: var(--data-table-row-selected);
    border-left-color: var(--data-table-row-selected-accent);
  }

  /* ---------- Scroll container + tablestyle modifiers ---------- */

  .data-table-scroll {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    max-height: 60vh;
    overflow-y: auto;
    overflow-x: auto;
    background: var(--data-table-surface);
  }

  .data-table.standardtable .data-table-scroll {
    max-height: 70vh;
  }

  .data-table.nestedtable .data-table-scroll {
    max-height: none;
  }

  .data-table.halftable .data-table-scroll {
    max-height: 35vh;
  }

  .data-table-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-width: max-content;
  }

  /* ---------- Cells ---------- */

  .data-table-cell {
    flex: 1 1 0;
    /*
      border-box so the column's explicit/computed width is the cell's
      total outer width regardless of padding. Without this, display
      cells (padding 0.55/0.85) render ~27px wider than editor cells
      (padding 0) at the same width: 160px, which shifts every column
      after an editor cell in row-edit mode.
    */
    box-sizing: border-box;
    padding: 0.55rem 0.85rem;
    font-size: 0.875rem;
    line-height: 1.3;
    color: inherit;
    display: flex;
    align-items: center;
    gap: 0.35rem;
    min-width: 0;
    font-variant-numeric: tabular-nums;
  }

  .data-table-header .data-table-cell {
    background: var(--data-table-header-bg);
    color: var(--data-table-header-color);
    font-size: 0.72rem;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 0.55rem 0.85rem;
  }

  /* ---------- Sort indicators ---------- */

  .data-table-header .sorting {
    cursor: pointer;
    user-select: none;
    transition: color 120ms ease;
  }

  .data-table-header .sorting:hover {
    color: var(--data-table-text);
  }

  .data-table-header .sorting::after {
    content: '';
    display: inline-block;
    width: 0.55rem;
    height: 0.55rem;
    margin-left: auto;
    flex: 0 0 auto;
    background-image:
      linear-gradient(45deg, transparent 50%, currentColor 50%),
      linear-gradient(-45deg, transparent 50%, currentColor 50%);
    background-size: 0.32rem 0.32rem;
    background-position:
      0 0.18rem,
      0.23rem 0.18rem;
    background-repeat: no-repeat;
    opacity: 0.35;
    transition: opacity 120ms ease;
  }

  .data-table-header .sorting:hover::after {
    opacity: 0.65;
  }

  .data-table-header .sorting.sorting_asc,
  .data-table-header .sorting.sorting_desc {
    color: var(--data-table-accent-strong);
  }

  .data-table-header .sorting.sorting_asc::after {
    background-image: linear-gradient(45deg, transparent 50%, currentColor 50%);
    background-size: 0.45rem 0.45rem;
    background-position: 0 0.07rem;
    opacity: 1;
  }

  .data-table-header .sorting.sorting_desc::after {
    background-image: linear-gradient(
      -45deg,
      transparent 50%,
      currentColor 50%
    );
    background-size: 0.45rem 0.45rem;
    background-position: 0 0.07rem;
    opacity: 1;
  }

  /* ---------- Row extension ---------- */

  .data-table-row-extension {
    display: block;
    box-sizing: border-box;
    border-left: 3px solid var(--data-table-accent);
    border-bottom: 1px solid var(--data-table-divider);
    padding: 0.85rem 1rem;
    background: var(--data-table-row-extension-bg);
    color: var(--data-table-text);
  }

  /* ---------- Group header ---------- */

  .data-table-group-header-row {
    border-bottom: 1px solid var(--data-table-divider);
    background: var(--data-table-accent-soft);
    position: relative;
  }

  .data-table-group-header-cell {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.55rem 0.85rem;
    background: var(--data-table-accent-soft);
    border-left: 3px solid var(--data-table-accent);
    font-weight: 600;
    color: var(--brand-rose-700, var(--data-table-accent-strong));
    position: sticky;
    left: 0;
    z-index: 2;
  }

  /* Extend group header background to fill entire row during scroll */
  .data-table-group-header-cell::after {
    content: '';
    position: absolute;
    top: 0;
    left: 100%;
    width: 100vw;
    height: 100%;
    background: var(--data-table-accent-soft);
    z-index: -1;
  }

  .data-table-group-header-label {
    font-size: 0.78rem;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    white-space: nowrap;
  }

  .data-table-group-header-count {
    font-size: 0.72rem;
    font-weight: 500;
    color: var(--data-table-text-muted);
    white-space: nowrap;
  }

  /* ---------- Load-more row ---------- */

  .data-table-load-more {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 0.85rem 1rem;
    border-top: 1px solid var(--data-table-divider);
    background: var(--data-table-surface);
  }

  .data-table-load-more .load-more-button {
    border-radius: 999px;
    border: none;
    background: var(--data-table-accent);
    color: #fff;
    font-family: inherit;
    font-weight: 600;
    font-size: 0.85rem;
    padding: 0.5rem 1.5rem;
    min-width: 160px;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(225, 71, 126, 0.25);
    transition:
      transform 140ms ease,
      box-shadow 140ms ease,
      background 140ms ease;
  }

  .data-table-load-more .load-more-button:hover:not(:disabled) {
    background: var(--data-table-accent-strong);
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(225, 71, 126, 0.32);
  }

  .data-table-load-more .load-more-button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    box-shadow: none;
  }

  .data-table-load-more .load-more-count {
    position: absolute;
    left: 1rem;
    font-size: 0.78rem;
    color: var(--data-table-text-muted);
    font-weight: 500;
  }

  /* ---------- Fullscreen toggle ---------- */

  .data-table-fullscreen-toggle {
    position: absolute;
    right: 0.5rem;
    font-size: 1.1rem;
    color: var(--data-table-text-muted);
  }

  .data-table-fullscreen-toggle:hover {
    color: var(--data-table-accent);
  }

  :host(.data-table-fullscreen) {
    position: fixed !important;
    top: 0;
    left: 0;
    width: 100vw !important;
    height: 100vh !important;
    z-index: 9999;
    border-radius: 0;
    overflow: hidden;
  }

  :host(.data-table-fullscreen) .data-table.data-table {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    border-radius: 0;
    overflow: hidden;
  }

  :host(.data-table-fullscreen) .data-table-scroll {
    max-height: none !important;
    flex: 1 1 0;
    min-height: 0;
  }

  :host(.data-table-fullscreen) .data-table-load-more {
    flex: 0 0 auto;
  }

  /* ---------- Fixed columns ---------- */

  .data-table-cell--fixed {
    position: sticky;
    background: var(--data-table-surface);
    z-index: 2;
  }

  .data-table-content .data-table-row:hover .data-table-cell--fixed {
    background: var(--data-table-row-hover);
  }

  .data-table-content .data-table-row.selected .data-table-cell--fixed {
    background: var(--data-table-row-selected);
  }

  .data-table-header .data-table-cell--fixed {
    background: var(--data-table-header-bg);
    z-index: 5;
  }

  .data-table-cell--fixed-left {
    box-shadow: var(--data-table-row-shadow-fixed);
  }

  .data-table-cell--fixed-right {
    box-shadow: var(--data-table-row-shadow-fixed-right);
  }

  /* ---------- Alignment helpers ---------- */

  .text-start,
  .text-left {
    text-align: left;
    justify-content: flex-start;
  }

  .text-end,
  .text-right {
    text-align: right;
    justify-content: flex-end;
  }

  .text-center {
    text-align: center;
    justify-content: center;
  }

  /* ---------- Drag/drop affordances ---------- */

  .ghost-row {
    opacity: 0.4;
  }

  .drop-target-at {
    box-shadow: inset 0 -2px 0 var(--data-table-accent);
  }

  .no-drop-allowed {
    cursor: not-allowed;
  }

  .drag-grab-handle {
    color: var(--data-table-text-muted);
    cursor: grab;
  }

  .draggable-item {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 0.35rem;
  }

  /* Legacy width helpers retained for compatibility with row classes */
  .data-table .column,
  .data-table .col,
  .data-table .data-table-cell {
    overflow: hidden;
  }

  /* ---------- Inline editors (cell / row / table edit modes) ---------- */

  /*
    Editable display state — when a cell IS editable but isn't currently
    rendering as an editor (cell mode before activation, or row mode for
    non-selected rows). Three layered cues so a user can see at a glance
    which cells they can interact with:

      1. Subtle background tint (rose-50 at 35% opacity).
      2. Dashed bottom border in the accent colour.
      3. Stronger tint + solid border on hover.

    The body padding stays 0.55/0.85 to keep alignment with read-only
    cells. Cursor is "text" since a click drops into an editor.
  */
  .data-table-cell--editable {
    cursor: text;
    background: color-mix(
      in srgb,
      var(--data-table-accent-soft) 30%,
      transparent
    );
    box-shadow: inset 0 -1px 0 0
      color-mix(in srgb, var(--data-table-accent) 35%, transparent);
    transition:
      background 140ms ease,
      box-shadow 140ms ease;
  }

  .data-table-cell--editable:hover {
    background: color-mix(
      in srgb,
      var(--data-table-accent-soft) 70%,
      transparent
    );
    box-shadow: inset 0 -2px 0 0 var(--data-table-accent);
  }

  /*
    When the cell is currently rendering its editor we override the
    display-state cues above — the editor itself supplies the focus
    affordance.
  */
  .data-table-cell--editor {
    padding: 0;
    overflow: visible;
    background: var(--data-table-surface);
    box-shadow: none;
    position: relative;
    /*
      Cross-axis stretch so editors fill the row's height. Main-axis
      justification stays at the cell's default — that lets the
      column's align property (text-left / text-center / text-right)
      keep working for compact editors (sl-checkbox, sl-switch,
      sl-radio). Wide editors (sl-input, sl-select, sl-textarea)
      stretch via their own flex rule below.
    */
    align-items: stretch;
  }

  .data-table-cell--editor.data-table-cell--editable {
    background: var(--data-table-surface);
    box-shadow: none;
  }

  /*
    Active editor cell — the one the user is currently typing in. A
    solid 2px ring drawn inside the cell so it doesn't shift layout,
    plus a brighter background. Visible without ambiguity.
  */
  .data-table-cell--editor:focus-within {
    background: var(--data-table-surface);
    box-shadow:
      inset 0 0 0 2px var(--data-table-accent),
      0 4px 12px -2px rgba(225, 71, 126, 0.18);
    z-index: 1;
  }

  /*
    Shoelace inputs default to a bordered "card" look. Inside cells we want
    them to look like the cell itself — no border, no shadow, padding that
    matches the cell's display state, and a brand-rose underline only on
    focus. Same deal for select / textarea / checkbox.
  */
  .data-table-cell--editor sl-input,
  .data-table-cell--editor sl-select,
  .data-table-cell--editor sl-textarea {
    /*
      Wide editors fill the cell horizontally. flex: 1 1 100% sets
      flex-basis to 100% of the cell main axis (avoids the intrinsic-
      content sizing trap of display:flex children). align-self: stretch
      makes them fill cell height too.
    */
    display: block;
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    flex: 1 1 100%;
    align-self: stretch;
    --sl-input-border-color: transparent;
    --sl-input-border-color-hover: transparent;
    --sl-input-border-color-focus: transparent;
    --sl-input-border-width: 0;
    --sl-input-background-color: transparent;
    --sl-input-background-color-hover: transparent;
    --sl-input-background-color-focus: transparent;
    --sl-input-background-color-disabled: transparent;
    --sl-input-color: inherit;
    --sl-input-color-hover: inherit;
    --sl-input-color-focus: inherit;
    --sl-focus-ring-width: 0;
    --sl-input-focus-ring-color: transparent;
    --sl-input-required-content-color: var(--data-table-accent);
  }

  .data-table-cell--editor sl-input::part(form-control),
  .data-table-cell--editor sl-select::part(form-control),
  .data-table-cell--editor sl-textarea::part(form-control) {
    padding: 0;
    margin: 0;
    height: 100%;
  }

  .data-table-cell--editor sl-input::part(base),
  .data-table-cell--editor sl-select::part(combobox),
  .data-table-cell--editor sl-textarea::part(base) {
    background: transparent;
    border: none;
    border-radius: 0;
    box-shadow: none;
    min-height: 100%;
    /*
      Match the display-cell padding exactly so column content lines up
      identically across display ↔ editor states.
    */
    padding: 0.55rem 0.85rem;
  }

  .data-table-cell--editor sl-input::part(input),
  .data-table-cell--editor sl-textarea::part(textarea) {
    font-family: inherit;
    font-size: 0.875rem;
    color: inherit;
    padding: 0;
  }

  .data-table-cell--editor sl-textarea::part(textarea) {
    min-height: 1.5rem;
  }

  /*
    Compact editors keep their intrinsic width and follow the cell's
    main-axis justification (set by .text-center / .text-left /
    .text-right via the column align property). align-self: center
    so they stay vertically centred in the row regardless of stretch.
  */
  .data-table-cell--editor sl-checkbox,
  .data-table-cell--editor sl-switch,
  .data-table-cell--editor sl-radio-group {
    flex: 0 0 auto;
    align-self: center;
    --sl-input-border-color: var(--data-table-border);
    --sl-input-focus-ring-color: rgba(225, 71, 126, 0.25);
  }

  /* Right-align numeric editors when the column requested it. */
  .data-table-cell--editor.text-right sl-input::part(input),
  .data-table-cell--editor.text-end sl-input::part(input) {
    text-align: right;
  }

  .data-table-cell--editor.text-center sl-input::part(input) {
    text-align: center;
  }

  /* ---------- Add-row zone ---------- */

  .data-table-add-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.45rem;
    width: 100%;
    min-width: max-content;
    padding: 0.85rem 1rem;
    background: var(--data-table-surface);
    border-top: 1px dashed var(--data-table-border);
    color: var(--data-table-text-muted);
    font-size: 0.85rem;
    font-weight: 500;
    cursor: pointer;
    transition:
      background 140ms ease,
      color 140ms ease,
      border-color 140ms ease;
    user-select: none;
  }

  .data-table-add-row:hover,
  .data-table-add-row:focus-visible {
    background: var(--data-table-row-hover);
    color: var(--data-table-accent-strong);
    border-top-color: var(--data-table-accent);
    outline: none;
  }

  .data-table-add-row-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.1rem;
    height: 1.1rem;
    border-radius: 999px;
    background: var(--data-table-accent-soft);
    color: var(--data-table-accent-strong);
    font-weight: 700;
    font-size: 0.95rem;
    line-height: 1;
  }

  .data-table-add-row:hover .data-table-add-row-icon,
  .data-table-add-row:focus-visible .data-table-add-row-icon {
    background: var(--data-table-accent);
    color: #ffffff;
  }

  .data-table-add-row-label {
    letter-spacing: 0.01em;
  }

  /* ---------- Paginator row + pager ---------- */

  .data-table-paginator-row {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    padding: 0.55rem 0.85rem;
    border-top: 1px solid var(--data-table-divider);
    background: var(--data-table-surface);
  }

  app-data-table-paginator {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }

  app-data-table-paginator nav {
    display: flex;
    justify-content: center;
  }

  app-data-table-paginator .pagination {
    display: flex;
    list-style: none;
    margin: 0;
    padding: 0;
    gap: 0.25rem;
    align-items: center;
  }

  app-data-table-paginator .page-item {
    display: flex;
  }

  app-data-table-paginator .page-item .page-link {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 2rem;
    padding: 0 0.6rem;
    font-size: 0.82rem;
    font-weight: 500;
    color: var(--data-table-text);
    background: transparent;
    border: 1px solid var(--data-table-border);
    border-radius: var(--sl-border-radius-medium, 8px);
    text-decoration: none;
    cursor: pointer;
    transition:
      background 140ms ease,
      border-color 140ms ease,
      color 140ms ease,
      box-shadow 140ms ease;
  }

  app-data-table-paginator .page-item .page-link:hover {
    background: var(--brand-rose-50, var(--data-table-row-hover));
    border-color: var(--data-table-accent);
    color: var(--data-table-accent-strong);
  }

  app-data-table-paginator .page-item.active .page-link {
    background: var(--data-table-accent);
    color: #fff;
    border-color: transparent;
    font-weight: 600;
    cursor: default;
    box-shadow: 0 2px 8px rgba(225, 71, 126, 0.3);
  }

  app-data-table-paginator .page-item.disabled .page-link {
    color: var(--data-table-text-muted);
    background: transparent;
    border-color: var(--data-table-border);
    cursor: not-allowed;
    opacity: 0.5;
  }

  app-data-table-paginator .page-item.disabled .page-link:hover {
    background: transparent;
    border-color: var(--data-table-border);
    color: var(--data-table-text-muted);
    box-shadow: none;
  }
`;
